import type {Element, Root} from 'hast'
import {visit} from 'unist-util-visit'

import {delimiterProperty} from './delimiters'

/** Child tag names that are valid inside each table-structural element. */
const validTableChildren: Record<string, Set<string>> = {
  table: new Set(['caption', 'colgroup', 'thead', 'tbody', 'tfoot', 'tr']),
  thead: new Set(['tr']),
  tbody: new Set(['tr']),
  tfoot: new Set(['tr']),
  tr: new Set(['td', 'th']),
}

const isDelimiterElement = (node: Element) => delimiterProperty in (node.properties ?? {})

/**
 * Remove delimiter elements that are invalid direct children of table-structural elements.
 *
 * The Markdown delimiter extensions surface source tokens (such as the line prefix before a table's delimiter row) as
 * elements. Some of these end up as direct children of `table`/`thead`/`tbody`/`tfoot`/`tr`, which is invalid HTML.
 * When the document also contains raw HTML, the tree is reparsed by `hast-util-raw` (parse5), whose foster-parenting
 * algorithm relocates these invalid children to just before the `table`. That produces a tree where a node with a
 * larger source offset precedes the `table` node, so decorations get added to CodeMirror's `RangeSetBuilder` out of
 * order and it throws.
 *
 * These delimiters are never rendered anyway (the table is either replaced by a widget or reverts to plain document
 * text), so removing them is safe and keeps sibling nodes in source order.
 */
export function hastRemoveInvalidTableDelimiters(tree: Root): void {
  visit(tree, 'element', (node: Element) => {
    const validChildren = validTableChildren[node.tagName]
    if (!validChildren) return

    node.children = node.children.filter(
      child => !(child.type === 'element' && isDelimiterElement(child) && !validChildren.has(child.tagName)),
    )
  })
}
